// -----------------------------------------------------------------
// <copyright                                                       
//   file="Izenda.LicenseValidationInput.js"                        
//   company="Izenda">                                              
//  Copyright (c) 2015 Izenda, Inc.                                 
//  ALL RIGHTS RESERVED                                             
//                                                                  
//  The entire contents of this file is protected by U.S. and       
//  International Copyright Laws. Unauthorized reproduction,        
//  reverse-engineering, and distribution of all or any portion of  
//  the code contained in this file is strictly prohibited and may  
//  result in severe civil and criminal penalties and will be       
//  prosecuted to the maximum extent possible under the law.        
//                                                                  
//  RESTRICTIONS                                                    
//                                                                  
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES           
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE                          
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS              
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET    
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.                 
//                                                                  
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED      
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE        
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE       
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT  
//  AND PERMISSION FROM IZENDA INC.                                 
//                                                                  
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  
//  ADDITIONAL RESTRICTIONS.                                        
// </copyright>                                                     
// -----------------------------------------------------------------

import 'share/utils/Izenda.StringUtilities';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import localizer from 'share/localization/Izenda.Localizer';
import connect from 'share/enhancers/Izenda.Connect';
import LicenseInfoConstants from 'logic/data/Izenda.LicenseInfoConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import SecuredInput from 'share/components/controls/Izenda.SecuredInput';

const ValidatableSecuredInput = Validator()(SecuredInput);

/**
 * License Validation component
 */
@connect()
export default class LicenseValidationInput extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired
  };

  render() {
    const { props } = this;
    const { item: licenseInfo } = props;
    const isOffline = !licenseInfo.licenseOnlineMode;

    const inputClasses = classnames('form-control', {
      'izenda-LicenseChecker-validationInput': licenseInfo.isValidLicense
    });

    return (
      <fieldset className='form-horizontal izenda-LicenseValidation'
                disabled={props.validatedConnection ? null : 'disabled'}>
        {/*license mode*/}
        <div className="form-group">
          <label className="control-label col-md-2">{localizer.getText("LICENSE_CHECKER_LABEL_LICENSE_MODE")}</label>
          <div className="col-md-10">
            <div className="u_table">
              <span className="u_tableCell u_alignMiddle">{localizer.getText("LICENSE_CHECKER_LABEL_OFFLINE")}</span>
              <div className="izenda-Switch u_marginLeftRight">
                <input
                  type="checkbox"
                  id="izenda-Switch-checkbox"
                  className="izenda-Switch-checkbox"
                  onChange={this.onChangeLicenseMode}
                  checked={!isOffline}
                />
                <label className="izenda-Switch-label" htmlFor="izenda-Switch-checkbox"/>
              </div>
              <span className="u_tableCell u_alignMiddle">{localizer.getText("LICENSE_CHECKER_LABEL_ONLINE")}</span>
            </div>
          </div>
        </div>

        {/*license key*/}
        <div className='form-group'>
          <label
            className="col-md-2 control-label validation required">{localizer.getText("LICENSE_CHECKER_LABEL_LICENSE_KEY")}</label>
          <div className="col-md-10">
            <ValidatableSecuredInput
              className={inputClasses}
              onReset={this.resetLicenseInfo}
              onChange={this.onChangeLicenseKey}
              value={licenseInfo.licenseKey}
              secure={licenseInfo.validatedLicense}
              validations={LicenseInfoConstants.LICENSE_KEY_CONSTRAINT}
              field='licenseKey'
              maxLength='MAX'
              autoComplete={'off'}
            />
            {this.renderSuccessIcon(licenseInfo)}
          </div>
        </div>

        {/*token*/}
        {isOffline && (
          <div className='form-group'>
            <label
              className="col-md-2 control-label validation required">{localizer.getText("LICENSE_CHECKER_LABEL_TOKEN")}</label>
            <div className="col-md-10">
              <ValidatableSecuredInput
                className={inputClasses}
                onReset={this.resetLicenseInfo}
                onChange={this.onChangeToken}
                value={licenseInfo.tokenKey}
                secure={licenseInfo.validatedLicenseToken}
                validations={LicenseInfoConstants.TOKEN_CONSTRAINT}
                field='tokenKey'
                maxLength='MAX'
                autoComplete={'off'}
              />
              {this.renderSuccessIcon(licenseInfo)}
            </div>
          </div>)
        }

        {this.renderLicenseStatus()}

      </fieldset>
    );
  }

  renderSuccessIcon(licenseInfo) {
    return licenseInfo.isValidLicense
      ? (
        <div className='u-posAbsolute success-color u-iconAfterInput'>
          <span className="icon-ok"/>
        </div>
      )
      : null
  }

  renderLicenseStatus() {
    const { item: licenseInfo } = this.props;

    if (!licenseInfo.validatedLicense || !licenseInfo.isValidLicense) {
      return null;
    }

    // license will be valide in the future
    if (licenseInfo.numberOfDayToValid > 0) {
      return (
        <div className='form-group'>
          <div className="col-md-offset-2 col-md-10">
            <div className='alert alert-info' role='alert'>
              {String.format(LicenseInfoConstants.MESSAGE.MG07a,
                String.formatDate(licenseInfo.startDate),
                String.formatDate(licenseInfo.endDate))}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  onChangeLicenseMode = e => {
    const { props } = this;
    const { item: licenseInfo } = this.props;
    const newMode = e.target.checked;
    const getMessage = () => {
      const msgId = newMode ? 'MG01b' : 'MG01a';
      return LicenseInfoConstants.MESSAGE[msgId];
    };

    if (licenseInfo.validatedLicense && licenseInfo.isValidLicense) {
      //show message
      props.confirmFn('Confirmation', {
        description: getMessage()
      }).then(() => {
        this.updateLicenseMode(newMode);
        //reset validate value license/token
        this.resetLicenseValidation();
      });
    } else {
      this.updateLicenseMode(newMode);
    }
  };

  resetLicenseInfo = () => {
    const { props } = this;
    const { validatedLicense } = this.props.item;
    if (validatedLicense) {
      this.updateLicenseInfo('licenseKey', '');
      this.updateLicenseInfo('tokenKey', '');
      props.validation.reset('licenseKey');
      props.validation.reset('tokenKey');
    }
  };

  onChangeLicenseKey = e => {
    this.updateLicenseInfo('licenseKey', e.target.value);
  };

  onChangeToken = e => {
    this.updateLicenseInfo('tokenKey', e.target.value);
  };

  updateLicenseMode(licenseMode) {
    this.updateLicenseInfo('licenseOnlineMode', licenseMode);
    this.updateLicenseInfo('licenseKey', '');
    this.updateLicenseInfo('tokenKey', '');
  }

  updateLicenseInfo(key, value) {
    const { item: licenseInfo } = this.props;
    licenseInfo.updateLicenseInfoByKey(key, value);
  }

  resetLicenseValidation() {
    const valManager = this.props.validation;
    valManager.reset('licenseKey');
    valManager.reset('tokenKey');
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/LicenseChecker/Izenda.LicenseValidationInput.js